import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, MenuItem, CardContent, Typography, Box, Fade, FormControlLabel, Checkbox, Button } from '@mui/material';
import { LocationCity, Add, Public, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useDistrict } from 'hooks/api/terminology/district/useDistrict';
import { AnimatedButton, StyledCard, StyledTextField, globalColor } from '../../ConstantColors';
import { logger } from 'utils/environment/log';
import Swal from 'sweetalert2';

const AddEditDistrict = ({ editingDistrict, onDistrictSubmitted, provinces }) => {
    const [formData, setFormData] = useState({
        name: '',
        id: '',
        code: '',
        localName: '',
        description: '',
        isActive: true,
        longitude: 0,
        latitude: 0,
        province: {
            id: ''
        }
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const { createDistrict, updateDistrict } = useDistrict();

    // Initialize form data when editing
    useEffect(() => {
        if (editingDistrict) {
            setFormData({
                name: editingDistrict.name || '',
                id: editingDistrict.id || '',
                code: editingDistrict.code || '',
                localName: editingDistrict.localName || '',
                description: editingDistrict.description || '',
                isActive: editingDistrict.isActive !== undefined ? editingDistrict.isActive : true,
                longitude: editingDistrict.longitude || 0,
                latitude: editingDistrict.latitude || 0,
                province: {
                    id: editingDistrict.province?.id || ''
                }
            });
        } else {
            // Reset form for new district
            setFormData({
                name: '',
                id: '',
                code: '',
                localName: '',
                description: '',
                isActive: true,
                longitude: 0,
                latitude: 0,
                province: {
                    id: ''
                }
            });
        }
        setErrors({});
    }, [editingDistrict]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'District name is required';
        }

        if (!editingDistrict && !formData.id.trim()) {
            newErrors.id = 'District ID is required';
        }

        if (!formData.code.trim()) {
            newErrors.code = 'District code is required';
        }

        if (!formData.province.id) {
            newErrors.province = 'Province is required';
        }

        // Validate longitude range
        if (formData.longitude < -180 || formData.longitude > 180) {
            newErrors.longitude = 'Longitude must be between -180 and 180';
        }

        // Validate latitude range
        if (formData.latitude < -90 || formData.latitude > 90) {
            newErrors.latitude = 'Latitude must be between -90 and 90';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData((prev) => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }

        // Clear error when user starts typing
        if (errors[name] || errors[name?.split('.')[1]]) {
            setErrors((prev) => ({
                ...prev,
                [name]: '',
                [name?.split('.')[1]]: ''
            }));
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all required fields correctly.'
            });
            return;
        }

        setLoading(true);
        try {
            // Prepare data for submission
            const submitData = {
                ...formData,
                longitude: Number(formData.longitude),
                latitude: Number(formData.latitude)
            };

            if (editingDistrict) {
                await updateDistrict(editingDistrict.id, submitData);
                Swal.fire({
                    icon: 'success',
                    title: 'District Updated',
                    text: 'The district has been successfully updated.',
                    showConfirmButton: false,
                    timer: 1500
                });
            } else {
                await createDistrict(submitData);
                Swal.fire({
                    icon: 'success',
                    title: 'District Created',
                    text: 'The district has been successfully created.',
                    showConfirmButton: false,
                    timer: 1500
                });
            }
            onDistrictSubmitted();
        } catch (error) {
            logger.error('Error saving district:', error);

            let errorMessage = `Failed to ${editingDistrict ? 'update' : 'create'} the district. Please try again.`;
            if (error.response && error.response.data && error.response.data.message) {
                errorMessage = error.response.data.message;
            }

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        onDistrictSubmitted();
    };

    const getButtonText = () => {
        if (loading) return 'Saving...';
        return editingDistrict ? 'Update District' : 'Add District';
    };

    return (
        <Fade in timeout={1000}>
            <StyledCard elevation={0}>
                <CardContent>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: globalColor,
                            fontWeight: 'bold',
                            mb: 4
                        }}
                    >
                        <LocationCity /> {editingDistrict ? 'Edit District' : 'Add New District'}
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                name="name"
                                label="District Name *"
                                value={formData.name}
                                onChange={handleChange}
                                variant="outlined"
                                error={!!errors.name}
                                helperText={errors.name}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                name="id"
                                label="District ID *"
                                value={formData.id}
                                onChange={handleChange}
                                variant="outlined"
                                error={!!errors.id}
                                helperText={errors.id}
                                disabled={loading || editingDistrict} // Disable ID field when editing
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                name="code"
                                label="District Code *"
                                value={formData.code}
                                onChange={handleChange}
                                variant="outlined"
                                error={!!errors.code}
                                helperText={errors.code}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                select
                                fullWidth
                                name="province.id"
                                label="Province *"
                                value={formData.province.id}
                                onChange={handleChange}
                                variant="outlined"
                                error={!!errors.province}
                                helperText={errors.province}
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <Box sx={{ mr: 1, color: globalColor }}>
                                            <Public fontSize="small" />
                                        </Box>
                                    )
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select Province</em>
                                </MenuItem>
                                {provinces.map((province) => (
                                    <MenuItem key={province.id} value={province.id}>
                                        {province.name}
                                    </MenuItem>
                                ))}
                            </StyledTextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                name="localName"
                                label="Local Name"
                                value={formData.localName}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={loading}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.isActive}
                                            onChange={handleChange}
                                            name="isActive"
                                            disabled={loading}
                                            sx={{
                                                color: globalColor,
                                                '&.Mui-checked': {
                                                    color: globalColor
                                                }
                                            }}
                                        />
                                    }
                                    label="Active"
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                name="longitude"
                                label="Longitude"
                                type="number"
                                value={formData.longitude}
                                onChange={handleChange}
                                variant="outlined"
                                error={!!errors.longitude}
                                helperText={errors.longitude || 'Value between -180 and 180'}
                                disabled={loading}
                                inputProps={{
                                    min: -180,
                                    max: 180,
                                    step: 0.000001
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <StyledTextField
                                fullWidth
                                name="latitude"
                                label="Latitude"
                                type="number"
                                value={formData.latitude}
                                onChange={handleChange}
                                variant="outlined"
                                error={!!errors.latitude}
                                helperText={errors.latitude || 'Value between -90 and 90'}
                                disabled={loading}
                                inputProps={{
                                    min: -90,
                                    max: 90,
                                    step: 0.000001
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <StyledTextField
                                fullWidth
                                name="description"
                                label="Description"
                                value={formData.description}
                                onChange={handleChange}
                                variant="outlined"
                                multiline
                                rows={3}
                                disabled={loading}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    disabled={loading}
                                    sx={{
                                        borderColor: globalColor,
                                        color: globalColor,
                                        '&:hover': {
                                            borderColor: globalColor,
                                            backgroundColor: `${globalColor}10`
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                                <AnimatedButton
                                    variant="contained"
                                    startIcon={editingDistrict ? <SaveIcon /> : <Add />}
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    size="large"
                                >
                                    {getButtonText()}
                                </AnimatedButton>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </StyledCard>
        </Fade>
    );
};

AddEditDistrict.propTypes = {
    editingDistrict: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        code: PropTypes.string,
        localName: PropTypes.string,
        description: PropTypes.string,
        isActive: PropTypes.bool,
        longitude: PropTypes.number,
        latitude: PropTypes.number,
        province: PropTypes.shape({
            id: PropTypes.string
        })
    }),
    onDistrictSubmitted: PropTypes.func.isRequired,
    provinces: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired
        })
    ).isRequired
};

export default AddEditDistrict;
